import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { TrendingTopic, SchoolEvent, CommunityHighlight } from '../types';

interface TrendingSidebarProps {
  trendingTopics: TrendingTopic[];
  upcomingEvents: SchoolEvent[];
  communityHighlights: CommunityHighlight[];
  onTopicClick: (topic: string) => void;
  selectedTopic?: string | null;
  onEventAttend: (eventId: string) => void;
}

const TrendingSidebar: React.FC<TrendingSidebarProps> = ({
  trendingTopics,
  upcomingEvents,
  communityHighlights,
  onTopicClick,
  selectedTopic,
  onEventAttend,
}) => {
  const location = useLocation();

  const formatEventDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleDateString('ro-RO', options);
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="TrendingUp" size={20} className="text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Subiecte în trend</h3>
        </div>
        {(selectedTopic || '').toString().trim() ? (
          <div className="mb-3 flex items-center justify-between gap-2 text-xs">
            <div className="text-muted-foreground truncate">
              Filtru: <span className="text-foreground font-medium">#{String(selectedTopic).trim()}</span>
            </div>
            <button
              type="button"
              className="px-2 py-1 rounded-md border border-border hover:bg-muted flex-shrink-0"
              onClick={() => onTopicClick(String(selectedTopic))}
            >
              Elimină
            </button>
          </div>
        ) : null}
        <div className="space-y-2">
          {trendingTopics.map((topic) => (
            <button
              key={topic.id}
              onClick={() => onTopicClick(topic.name)}
              className={`w-full flex items-center justify-between p-2 rounded-md transition-colors duration-150 text-left ${
                (selectedTopic || '').toLowerCase() === (topic.name || '').toLowerCase()
                  ? 'bg-primary/10 hover:bg-primary/15'
                  : 'hover:bg-muted'
              }`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  #{topic.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {topic.postCount} postări • {topic.category}
                </p>
              </div>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
            </button>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Calendar" size={20} className="text-accent" />
          <h3 className="text-sm font-semibold text-foreground">Evenimente viitoare</h3>
        </div>
        <div className="space-y-3">
          {upcomingEvents.map((event) => (
            <div
              key={event.id}
              className="p-3 bg-muted rounded-md hover:bg-muted/80 transition-colors duration-150"
            >
              <div className="flex items-start gap-2 mb-2">
                <div className="flex-shrink-0 w-10 h-10 bg-accent/10 rounded-md flex items-center justify-center">
                  <Icon name="Calendar" size={20} className="text-accent" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-foreground mb-1">
                    {event.title}
                  </h4>
                  <p className="text-xs text-muted-foreground mb-1">
                    {formatEventDate(event.date)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <Icon name="MapPin" size={12} className="inline mr-1" />
                    {event.location}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-muted-foreground">
                  {event.attendees} participă
                </span>
                <Button
                  variant={event.isAttending ? 'outline' : 'default'}
                  size="xs"
                  onClick={() => onEventAttend(event.id)}
                  iconName={event.isAttending ? 'Check' : 'Plus'}
                >
                  {event.isAttending ? 'Participi' : 'Participă'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Award" size={20} className="text-success" />
          <h3 className="text-sm font-semibold text-foreground">Repere din comunitate</h3>
        </div>
        <div className="space-y-3">
          {communityHighlights.map((highlight) => (
            <div
              key={highlight.id}
              className="group cursor-pointer"
            >
              <div className="relative overflow-hidden rounded-md aspect-video mb-2">
                <Image
                  src={highlight.image}
                  alt={highlight.imageAlt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <span className="px-2 py-1 bg-success/90 text-white text-xs rounded-md">
                    {highlight.category}
                  </span>
                </div>
              </div>
              <h4 className="text-sm font-medium text-foreground mb-1 group-hover:text-primary transition-colors duration-150">
                {highlight.title}
              </h4>
              <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                {highlight.description}
              </p>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Icon name="Heart" size={12} />
                <span>{highlight.engagementCount} interacțiuni</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-border rounded-lg shadow-sm p-4">
        <div className="flex items-start gap-2 mb-3">
          <Icon name="Lightbulb" size={20} className="text-primary mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">
              Reguli comunitate
            </h3>
            <p className="text-xs text-muted-foreground">
              Păstrăm comunitatea școlii sigură și respectuoasă. Raportează conținutul nepotrivit.
            </p>
          </div>
        </div>
        <Button asChild variant="outline" size="sm" fullWidth iconName="Shield">
          <Link to="/legal/regulament-platforma" state={{ backgroundLocation: location }}>
            Vezi regulile
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default TrendingSidebar;
